import Radium from '@flipdish/radium';
import React from 'react';
import { FormGroup } from 'react-bootstrap';
import { getTranslate } from 'react-localize-redux';
import { connect } from 'react-redux';
import { Action } from 'redux';
import { ThunkDispatch } from 'redux-thunk';
import { Track } from '../../analytics';
import * as Api from '../../Api';
import { BLANK } from '../../constants/Constants';
import * as Data from '../../Data';
import { menuSelectors } from '../../selectors';
import * as Store from '../../Store';
import { Utilities } from '../../Utilities';

const tinycolor = require('tinycolor2');

interface PreorderProps {
  DeliveryLocation: Api.DeliveryLocation | null;
  Restaurant: ReturnType<typeof menuSelectors.getRestaurantDetails>;
  RequestedDeliveryOrPickupTimeUtc: string;
  ChangeDeliveryTime: (time: string) => any;
  PickupLocationType: Api.PickupLocationType;
  AppConfig: Data.Async<Api.AppConfig>;
  AppDesignScheme: Api.AppDesignScheme;
  isDark: boolean;
  type: 'collection' | 'delivery';
  disabled?: boolean;
}

interface State {
  storedTime: string;
}

type Props = MapStateToProps & MapDispatchToProps & PreorderProps;

export class Preorder extends React.Component<Props, State> {
  constructor(props: Props, context: any) {
    super(props, context);
    this.state = {
      storedTime: ''
    };
  }

  public changeDeliveryTime = (e: React.ChangeEvent<HTMLSelectElement>) => {
    Track('Orders', 'Change Collection Time');
    this.props.ChangeDeliveryTime((e.target as any).value);
  };

  // We need to force the 'RequestedDeliveryOrPickupTimeUtc' time to not be null.
  // Set it to the first item in the option list.
  public forcePreOrderSelection = (props: Props) => {
    const { Restaurant, RequestedDeliveryOrPickupTimeUtc, ChangeDeliveryTime } = props;

    if (!Restaurant) {
      return;
    }

    const PreOrder = !Restaurant.IsOpen && Restaurant.AcceptPreOrders;

    const AllowPreOrdersAndTableService =
      Restaurant.DeliveryType === Api.DeliveryType.Collection
        ? Restaurant.AllowPreOrdersAndTableService || true
        : true;

    //if restaurant does not allow simultaneous preorder and table service then switch the time dropdown
    if (
      PreOrder &&
      !AllowPreOrdersAndTableService &&
      props.PickupLocationType === Api.PickupLocationType.TableService &&
      (RequestedDeliveryOrPickupTimeUtc != BLANK || !RequestedDeliveryOrPickupTimeUtc)
    ) {
      this.setState({ storedTime: RequestedDeliveryOrPickupTimeUtc }, () => {
        ChangeDeliveryTime(BLANK);
      });
      return;
    }

    if (
      props.PickupLocationType != Api.PickupLocationType.TableService &&
      this.state.storedTime != ''
    ) {
      const oldStoredTime = this.state.storedTime;
      this.setState({ storedTime: '' }, () => {
        ChangeDeliveryTime(oldStoredTime);
      });
    }

    if (RequestedDeliveryOrPickupTimeUtc) {
      return;
    }

    const { PreOrderOptions } = Restaurant;

    if (!PreOrderOptions) {
      return;
    }

    if (PreOrderOptions.PreOrderTimes.length < 1) {
      return;
    }

    const [defaultTime] = PreOrderOptions.PreOrderTimes;

    // Important!
    // Don't dispatch an update to Redux that will trigger componentWillReceiveProps()
    // from inside componentWillReceiveProps unless we need to. Without this
    // guard React will recurse until the stack is exceeded, and the app crashes.
    if (defaultTime.Key === RequestedDeliveryOrPickupTimeUtc) {
      return;
    }

    if (!PreOrderOptions.RequireExplicitSelect) {
      ChangeDeliveryTime(defaultTime.Key);
    } else {
      ChangeDeliveryTime(BLANK);
    }
  };

  public preorderTitle = (type: Api.DeliveryType, dl: Api.DeliveryLocation | null): string => {
    if (type == Api.DeliveryType.Delivery) {
      if (dl) {
        return this.props.translate('Deliver_to_checkout', {
          address: dl.Building + ' ' + dl.Street
        }) as string;
      }
      return this.props.translate('Deliver') as string;
    }

    const { Restaurant } = this.props;

    if (!Restaurant) {
      return this.props.translate('Collect') as string;
    }

    const tableServiceIndex =
      Restaurant.DeliveryType === Api.DeliveryType.Collection &&
      Restaurant.PickupLocationTypes.findIndex(
        (PickupLocation) => PickupLocation === Api.PickupLocationType.TableService
      );

    const isTableServiceOnly =
      Restaurant.DeliveryType === Api.DeliveryType.Collection &&
      Restaurant.PickupLocationTypes.length === 1 &&
      tableServiceIndex != -1;

    const TableServiceCategory =
      Restaurant.DeliveryType === Api.DeliveryType.Collection
        ? Restaurant.TableServiceCatagory || Api.TableServiceCategory.Generic
        : Api.TableServiceCategory.Generic;

    // No Dine-in only option yet!
    const isDineInOnly =
      Restaurant.DeliveryType === Api.DeliveryType.Collection &&
      Restaurant.PickupLocationTypes.length === 1 &&
      Restaurant.PickupLocationTypes[0] === 2;

    const getTableServiceMessage = () => {
      switch (TableServiceCategory) {
        case Api.TableServiceCategory.Generic:
          return this.props.translate('Order_from_checkout', {
            restaurantName: Restaurant.RestaurantName
          }) as string;
        case Api.TableServiceCategory.Villa:
          return this.props.translate('Villa_service_from_checkout', {
            restaurantName: Restaurant.RestaurantName
          }) as string;
        case Api.TableServiceCategory.House:
          return this.props.translate('House_service_from_checkout', {
            restaurantName: Restaurant.RestaurantName
          }) as string;
        case Api.TableServiceCategory.Room:
          return this.props.translate('Room_service_from_checkout', {
            restaurantName: Restaurant.RestaurantName
          }) as string;
        case Api.TableServiceCategory.Area:
          return this.props.translate('Area_service_from_checkout', {
            restaurantName: Restaurant.RestaurantName
          }) as string;
        case Api.TableServiceCategory.Table:
          return this.props.translate('Table_service_from_checkout', {
            restaurantName: Restaurant.RestaurantName
          }) as string;
        case Api.TableServiceCategory.ParkingBay:
          return this.props.translate('Parking_bay_service_from_checkout', {
            restaurantName: Restaurant.RestaurantName
          }) as string;
        case Api.TableServiceCategory.Gate:
          return this.props.translate('Gate_service_from_checkout', {
            restaurantName: Restaurant.RestaurantName
          }) as string;
        case Api.TableServiceCategory.DriveThrough:
          return this.props.translate('Drive_Through_from_checkout', {
            restaurantName: Restaurant.RestaurantName
          }) as string;
        default:
          return this.props.translate('Order_from_checkout', {
            restaurantName: Restaurant.RestaurantName
          }) as string;
      }
    };

    if (isTableServiceOnly) {
      return getTableServiceMessage();
    } else if (isDineInOnly) {
      // No Dine-in only option yet!
      return this.props.translate('Dine_in_with_checkout', {
        restaurantName: Restaurant.RestaurantName
      }) as string;
    } else {
      // add scenarios here for: 1) colletion / dine-in here, 2) collection / table service
      return this.props.translate('Collect_from_checkout', {
        restaurantName: Restaurant.RestaurantName
      }) as string;
    }
  };

  public UNSAFE_componentWillReceiveProps(nextProps: Props) {
    this.forcePreOrderSelection(nextProps);
  }

  public UNSAFE_componentWillMount() {
    this.forcePreOrderSelection(this.props);
  }

  public render(): JSX.Element | null {
    const {
      Restaurant,
      RequestedDeliveryOrPickupTimeUtc,
      DeliveryLocation,
      AppDesignScheme,
      isDark,
      AppConfig,
      translate,
      disabled
    } = this.props;

    const styles = {
      primary: {
        color: AppDesignScheme.Primary
      },
      tertiary: {
        color: AppDesignScheme.Tertiary
      },
      formControl: {
        color: AppDesignScheme.Tertiary,
        background: AppDesignScheme.Primary,
        borderColor: tinycolor(AppDesignScheme.Tertiary)
          .setAlpha(0.2)
          .toString(),
        ':focus': {
          borderColor: AppDesignScheme.Secondary
        }
      },
      darkFormControl: {
        color: AppDesignScheme.Primary,
        background: AppDesignScheme.Tertiary,
        borderColor: AppDesignScheme.Primary
      },
      primaryBackground: {
        background: AppDesignScheme.Primary
      },
      tertiaryBackground: {
        background: AppDesignScheme.Tertiary
      }
    };

    if (
      !Restaurant ||
      !Restaurant.AcceptPreOrders ||
      !Restaurant.PreOrderOptions ||
      AppConfig.status != 'success'
    ) {
      return null;
    }

    let times = Restaurant.PreOrderOptions.PreOrderTimes.map((t) => {
      return (
        <option
          key={t.Key}
          value={t.Key}
          style={isDark ? styles.tertiaryBackground : styles.primaryBackground}
          disabled={t.IsFull}
        >
          {t.IsFull ? `${t.Value} (${translate('Full')})` : t.Value}
        </option>
      );
    });

    const tableServiceIndex =
      Restaurant.DeliveryType === Api.DeliveryType.Collection &&
      Restaurant.PickupLocationTypes.findIndex(
        (PickupLocation) => PickupLocation === Api.PickupLocationType.TableService
      );

    const isTableServiceOnly =
      Restaurant.DeliveryType === Api.DeliveryType.Collection &&
      Restaurant.PickupLocationTypes.length === 1 &&
      tableServiceIndex != -1;

    const TableServiceCategory =
      Restaurant.DeliveryType === Api.DeliveryType.Collection
        ? Restaurant.TableServiceCatagory || Api.TableServiceCategory.Generic
        : Api.TableServiceCategory.Generic;

    const getTableServiceMessage = () => {
      switch (TableServiceCategory) {
        case Api.TableServiceCategory.Generic:
        case Api.TableServiceCategory.Table:
          return this.props.translate('Select_time_for_table_service') as string;
        case Api.TableServiceCategory.Villa:
          return this.props.translate('Select_time_for_villa_service') as string;
        case Api.TableServiceCategory.House:
          return this.props.translate('Select_time_for_house_service') as string;
        case Api.TableServiceCategory.Room:
          return this.props.translate('Select_time_for_room_service') as string;
        case Api.TableServiceCategory.Area:
          return this.props.translate('Select_time_for_area_service') as string;
        case Api.TableServiceCategory.ParkingBay:
          return this.props.translate('Select_time_for_parking_bay_service') as string;
        case Api.TableServiceCategory.Gate:
          return this.props.translate('Select_time_for_gate_service') as string;
        case Api.TableServiceCategory.DriveThrough:
          return this.props.translate('Select_time_for_Drive_Through') as string;
        default:
          return this.props.translate('Select_time_for_order') as string;
      }
    };

    const CollectionTimeMessage = isTableServiceOnly
      ? getTableServiceMessage()
      : AppConfig.data.DineInEnabled && !AppConfig.data.PickupEnabled
      ? translate('Select_time_to_dine_in')
      : AppConfig.data.DineInEnabled && AppConfig.data.PickupEnabled
      ? translate('Select_collection_dinein_time')
      : translate('Select_collection_time');

    let extraOption = (
      <option
        key={BLANK}
        value={BLANK}
        disabled
        style={isDark ? styles.tertiaryBackground : styles.primaryBackground}
      >
        {Utilities.preorderInfoText(Restaurant.DeliveryType) == '-Select Delivery Time-'
          ? `-${translate('Select_delivery_time')}-`
          : `-${CollectionTimeMessage}-`}
      </option>
    );

    const PreOrder = !Restaurant.IsOpen && Restaurant.AcceptPreOrders;

    const AllowPreOrdersAndTableService =
      Restaurant.DeliveryType === Api.DeliveryType.Collection
        ? Restaurant.AllowPreOrdersAndTableService || true
        : true;

    //if restaurant does not allow simultaneous preorder and table service then have the time option changed
    if (
      PreOrder &&
      !AllowPreOrdersAndTableService &&
      this.props.PickupLocationType === Api.PickupLocationType.TableService
    ) {
      extraOption = (
        <option
          key={BLANK}
          value={BLANK}
          style={isDark ? styles.tertiaryBackground : styles.primaryBackground}
        >
          -----------------------
        </option>
      );
    }

    //Concatenate info value to the dropdown list
    times = [extraOption].concat(times);

    const title = this.preorderTitle(Restaurant.DeliveryType, DeliveryLocation);

    return (
      <FormGroup>
        {AppConfig.data.DeliveryAddressLayout == 2 &&
        DeliveryLocation &&
        this.props.type === 'delivery' ? (
          <div className="flex">
            <h2
              className="tertiary bold clear-margin-top fd-info"
              style={isDark ? styles.primary : styles.tertiary}
            >
              {this.props.translate('Deliver_to_checkout', { address: ' ' })}
            </h2>
            <div className="margin-left-five flex flex-column">
              <h4
                className="tertiary bold clear-margins"
                style={isDark ? styles.primary : styles.tertiary}
              >
                {DeliveryLocation.Street + ','}
              </h4>
              {DeliveryLocation.Building ? (
                <h4
                  className="tertiary clear-margins bold"
                  style={isDark ? styles.primary : styles.tertiary}
                >
                  {DeliveryLocation.Building}
                </h4>
              ) : null}
              <h4
                className="tertiary clear-margin-top bold"
                style={isDark ? styles.primary : styles.tertiary}
              >
                {DeliveryLocation.Town}
                {!!DeliveryLocation.PostCode ? ` , ${DeliveryLocation.PostCode}` : ''}
              </h4>
            </div>
          </div>
        ) : (
          <h2
            className="tertiary bold clear-margin-top fd-info"
            style={isDark ? styles.primary : styles.tertiary}
          >
            {title}
          </h2>
        )}

        <select
          aria-label={translate('Select_time') as string}
          key="times-dropdown"
          className={`form-control ${
            RequestedDeliveryOrPickupTimeUtc === BLANK ? 'unselected-dropdown' : ''
          }`}
          onChange={this.changeDeliveryTime}
          value={RequestedDeliveryOrPickupTimeUtc}
          disabled={
            (PreOrder &&
              !AllowPreOrdersAndTableService &&
              this.props.PickupLocationType === Api.PickupLocationType.TableService) ||
            disabled
          }
          style={isDark ? styles.darkFormControl : styles.formControl}
        >
          {times}
        </select>
      </FormGroup>
    );
  }
}

type MapStateToProps = ReturnType<typeof mapStateToProps>;
const mapStateToProps = (state: Store.State) => {
  return {
    translate: getTranslate(state.locale)
  };
};

type MapDispatchToProps = ReturnType<typeof mapDispatchToProps>;
const mapDispatchToProps = (dispatch: ThunkDispatch<Store.State, {}, Action>) => {
  return {};
};

export default connect<{}, {}, PreorderProps>(
  mapStateToProps,
  mapDispatchToProps
)(Radium(Preorder));
